import { useContext, useEffect } from "react";
import { SocketContext } from "src/contexts/SocketContext";
import useSocketRegistration from "src/contexts/useSocketRegistration";
import useAppStore from "src/stores/appStore";
import { assistantIdSelector } from "src/stores/selectors/appSelectors";

const EVENTS = {
  REGISTER: "notifications:register",
  NOTIFY: "notifications:notify",
  HISTORY_COUNT: "notifications:history:count",
  HISTORY_LIST: "notifications:history:list",
  MARK_AS_READ: "notifications:mark-as-read",
  ERROR: "error"
};

const FIRST_PAGE = 1;

const useNotificationsSocket = () => {
  const assistantId = useAppStore(assistantIdSelector);
  const socket = useContext(SocketContext);
  const isRegistered = useSocketRegistration(socket, EVENTS.REGISTER, assistantId);

  useEffect(() => {
    if (!socket || !assistantId) return undefined;

    const handleHistoryList = ({ notifications, hasMore }) => {
      const {
        addNotifications,
        setHasMoreNotifications,
        setIsInitialNotificationsLoading,
        setIsFetchingNextNotificationsPage
      } = useAppStore.getState();
      addNotifications(notifications);
      setHasMoreNotifications(hasMore);
      setIsInitialNotificationsLoading(false);
      setIsFetchingNextNotificationsPage(false);
    };

    const handleHistoryCount = (count) => {
      useAppStore.getState().setNotificationsCount(count);
    };

    // The server pushes an updated unread count alongside every notify, so the
    // notification itself is all that needs handling here.
    const handleNotify = (notification) => {
      useAppStore.getState().upsertNotification(notification);
    };

    const handleSocketError = (error) => {
      console.error("Notifications socket error:", error);
    };

    const fetchNextPage = () => {
      const {
        notifications,
        notificationsPage,
        setIsFetchingNextNotificationsPage,
        incrementNotificationsPage
      } = useAppStore.getState();
      setIsFetchingNextNotificationsPage(true);
      socket.emit(EVENTS.HISTORY_LIST, {
        assistantId,
        pageNumber: notificationsPage + 1,
        fromDate: notifications.at(0)?.createdAt
      });
      incrementNotificationsPage();
    };

    const markAsRead = () => {
      socket.emit(EVENTS.MARK_AS_READ, { assistantId, untilDate: new Date().toISOString() });
      useAppStore.getState().setNotificationsCount(0);
    };

    socket.on(EVENTS.HISTORY_LIST, handleHistoryList);
    socket.on(EVENTS.HISTORY_COUNT, handleHistoryCount);
    socket.on(EVENTS.NOTIFY, handleNotify);
    socket.on(EVENTS.ERROR, handleSocketError);

    const { setFetchNextNotificationsPage, setMarkNotificationsAsReadViaSocket } = useAppStore.getState();
    setFetchNextNotificationsPage(fetchNextPage);
    setMarkNotificationsAsReadViaSocket(markAsRead);

    return () => {
      socket.off(EVENTS.HISTORY_LIST, handleHistoryList);
      socket.off(EVENTS.HISTORY_COUNT, handleHistoryCount);
      socket.off(EVENTS.NOTIFY, handleNotify);
      socket.off(EVENTS.ERROR, handleSocketError);
    };
  }, [socket, assistantId]);

  // Runs on every (re)registration — initial connect, agent switch, reconnect — so a
  // reconnect resyncs whatever arrived while disconnected. Fetching page 1 also makes
  // the server send the unread count.
  useEffect(() => {
    if (!isRegistered || !socket || !assistantId) return;

    const { notifications, setIsInitialNotificationsLoading, setNotificationsPage } = useAppStore.getState();
    setIsInitialNotificationsLoading(notifications.length === 0);
    setNotificationsPage(FIRST_PAGE);
    socket.emit(EVENTS.HISTORY_LIST, { assistantId, pageNumber: FIRST_PAGE });
  }, [isRegistered, socket, assistantId]);
};

export default useNotificationsSocket;
